<template>
  <div class="testimonials-section">
    <link href="https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap" rel="stylesheet">
    <h2 class="decorated-title"><i><b><u>What Our Clients Say</u></b></i></h2>
    <div class="testimonials-grid" 
         ref="testimonialsGrid"
         @mouseenter="stopAutoplay"
         @mouseleave="startAutoplay">
      <div class="testimonial" v-for="(testimonial, index) in testimonials" :key="testimonial.id || index">
        <div class="testimonial-content">
          <p>{{ testimonial.content }}</p>
          <div class="testimonial-author">
            <img :src="testimonial.avatar" :alt="testimonial.name">
            <div>
              <h4>{{ testimonial.name }}</h4>
              <p>From: {{ testimonial.country }}</p>
              <p>Visited: {{ testimonial.visitDate }}</p>
              <p>Itinerary: {{ testimonial.itinerary }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { userComments } from '../data/userCommentData';

const testimonials = ref([]);
const testimonialsGrid = ref(null);
const currentIndex = ref(0);
const isAutoPlay = ref(true);
let autoPlayTimer = null;

const startAutoplay = () => {
  if (!isAutoPlay.value) return;
  
  stopAutoplay(); // Clear any existing timer
  
  autoPlayTimer = setInterval(() => {
    if (testimonialsGrid.value) {
      currentIndex.value = (currentIndex.value + 1) % testimonials.value.length;
      const testimonialWidth = testimonialsGrid.value.querySelector('.testimonial').offsetWidth;
      const gap = 32; // 2rem gap
      
      // If we're at the last testimonial, first scroll to the end
      if (currentIndex.value === 0) {
        testimonialsGrid.value.scrollTo({
          left: testimonialsGrid.value.scrollWidth,
          behavior: 'smooth'
        });
        
        // After scrolling to the end, wait a bit and then jump to the start
        setTimeout(() => {
          testimonialsGrid.value.scrollTo({
            left: 0,
            behavior: 'auto'
          });
        }, 0);
      } else {
        testimonialsGrid.value.scrollTo({
          left: currentIndex.value * (testimonialWidth + gap),
          behavior: 'smooth'
        });
      }
    }
  }, 2000);
};

const stopAutoplay = () => {
  if (autoPlayTimer) {
    clearInterval(autoPlayTimer);
    autoPlayTimer = null;
  }
};

onMounted(() => {
  testimonials.value = userComments.testimonials;
  startAutoplay();
});

onUnmounted(() => {
  stopAutoplay();
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Noto+Sans&family=Roboto+Slab&display=swap');
.testimonials-section {
  padding: 1rem 1rem;
  background: var(--color-background);
  border-radius: 12px;
  margin: 1rem 0;
  overflow: hidden;
}

.testimonials-section h2 {
  text-align: center;
  margin-bottom: 1rem;
  font-family: serif;
  font-size: 4rem;
  color: var(--color-heading);
}

.testimonials-grid {
  display: flex;
  gap: 2rem;
  margin-top: 2rem;
  overflow-x: auto;
  padding: 1rem 0.5rem;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;
  max-width: 1200px;
  margin: 0 auto;
  scroll-behavior: smooth;
}

.testimonials-grid::-webkit-scrollbar {
  display: none;
}

.testimonial {
  background: var(--color-background-soft);
  border-radius: 12px;
  padding: 2rem;
  min-width: 300px;
  width: calc(33.333% - 1.33rem);
  flex-shrink: 0;
  scroll-snap-align: start;
}

.testimonial-content {
  position: relative;
}

.testimonial-content p {
  font-style: italic;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  font-size: 1rem;
}

.testimonial-author {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;
}

.testimonial-author img {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
}

.testimonial-author h4 {
  margin: 0;
  font-size: 1.1rem;
  color: var(--color-heading);
}

.testimonial-author p {
  margin: 0;
  color: var(--color-text-soft);
  font-size: 0.9rem;
}

/* Decorated title styles */
.decorated-title {
  position: relative;
  font-weight: bold;
  font-style: italic;
  font-family: Serif;
  text-decoration: underline;
  color: var(--color-heading);
}

@media (max-width: 768px) {
  .testimonials-section {
    padding: 1rem 0.5rem;
    margin: 1rem 0;
  }

  .testimonial {
    width: 85%;
    min-width: 280px;
  }

  .testimonials-section h2 {
    font-size: 2rem;
  }
}
</style> 